// Phase-103 · Founder/CEO approvals queue.
// Mounted at /dashboard/approvals, gated by <RoleGate allow={['founder','ceo']}>.
// Lists pending approvals; row click opens a side panel with Approve/Reject buttons.
import { useEffect, useState } from "react";
import { Loader2, CheckCircle2, XCircle, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import RoleGate from "../components/RoleGate";
import { Button } from "../components/ui/button";
import api from "../services/api";

function PrettyJson({ value }) {
  return (
    <pre className="bg-slate-900 text-slate-100 rounded-lg p-3 font-mono text-xs overflow-x-auto">
      {JSON.stringify(value || {}, null, 2)}
    </pre>
  );
}

function Inner() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [acting, setActing] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/approvals?status=pending&limit=100");
      setRows(data?.rows || []);
    } catch {
      toast.error("Couldn't load approvals");
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const decide = async (decision) => {
    if (!selected) return;
    setActing(decision);
    const prev = rows;
    setRows(rows.filter((r) => r.id !== selected.id));
    try {
      await api.post(`/approvals/${selected.id}/decide`, { decision, note: note || undefined });
      toast.success(`Approval ${decision}d`);
      setSelected(null); setNote("");
    } catch (e) {
      setRows(prev);
      toast.error(
        (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : null)
        || "Decision failed"
      );
    } finally { setActing(null); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="approvals-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-orange-50 border border-orange-200 rounded-xl flex items-center justify-center">
          <ClipboardList className="h-5 w-5 text-orange-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold font-['Outfit'] text-slate-900">Approvals queue</h1>
          <p className="text-xs text-slate-500">Pending approvals awaiting your decision.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* List */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="p-12 flex items-center justify-center text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center text-slate-400" data-testid="approvals-empty">
              <CheckCircle2 className="h-10 w-10 mx-auto mb-2 text-emerald-300" />
              <p>No pending approvals. You&apos;re all caught up.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr className="text-left">
                  <th className="px-4 py-3 font-semibold">Request</th>
                  <th className="px-4 py-3 font-semibold">Type</th>
                  <th className="px-4 py-3 font-semibold">Requested by</th>
                  <th className="px-4 py-3 font-semibold">When</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => { setSelected(r); setNote(""); }}
                    data-testid={`approval-row-${r.request_id}`}
                    className={`border-t border-slate-100 cursor-pointer hover:bg-orange-50/40 transition-colors ${selected?.id === r.id ? "bg-orange-50/60" : ""}`}
                  >
                    <td className="px-4 py-3 font-mono text-xs">{r.request_id}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-xs">
                        {r.type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      {r.requested_by_email}
                      <div className="text-xs text-slate-400">{r.requested_by_role}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {new Date(r.requested_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Side panel */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5"
             data-testid="approval-detail-panel">
          {!selected ? (
            <p className="text-sm text-slate-400 py-8 text-center">
              Select a request on the left to review.
            </p>
          ) : (
            <>
              <div className="mb-3">
                <div className="text-xs text-slate-500">Request</div>
                <div className="font-mono text-sm text-slate-800">{selected.request_id}</div>
              </div>
              <div className="mb-3">
                <div className="text-xs text-slate-500">Type · approver</div>
                <div className="text-sm">
                  <span className="font-medium">{selected.type}</span>
                  <span className="text-slate-400"> · {selected.approver_role_required}</span>
                </div>
              </div>
              <div className="mb-3">
                <div className="text-xs text-slate-500 mb-1">Payload</div>
                <PrettyJson value={selected.payload} />
              </div>
              <div className="mb-3">
                <label className="text-xs text-slate-500 mb-1 block">Note (optional)</label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  data-testid="approval-note-textarea"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={() => decide("approve")}
                  disabled={acting !== null}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1"
                  data-testid="approval-approve-btn"
                >
                  {acting === "approve" ? <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                        : <CheckCircle2 className="h-4 w-4 mr-2" />}
                  Approve
                </Button>
                <Button
                  onClick={() => decide("reject")}
                  disabled={acting !== null}
                  variant="outline"
                  className="border-rose-300 text-rose-600 hover:bg-rose-50 flex-1"
                  data-testid="approval-reject-btn"
                >
                  {acting === "reject" ? <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                       : <XCircle className="h-4 w-4 mr-2" />}
                  Reject
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ApprovalsQueue() {
  return (
    <RoleGate allow={["founder", "ceo"]}>
      <Inner />
    </RoleGate>
  );
}
